import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Mail,
  Phone,
  ChevronDown,
  ChevronUp,
  Share2,
  Heart,
  X,
  Loader2,
  ExternalLink,
} from "lucide-react";
import ClaimStatusBadge from "../ClaimStatusBadge";
import { FaInstagram } from "react-icons/fa";
import { formatCompactNumber } from "../../../utils/numberFormat";
import InviteInfluencerWizard from "../inviteInfluencer";
import {
  formatInfluencerRating,
  getEffectiveInfluencerRating,
} from "../../utils/rating";

import {
  getInfluencerContactInfo,
  getSavedInfluencerStatus,
  saveInfluencer,
  unsaveInfluencer,
} from "../../services/marketplaceApi";
import { socialAccountsService } from "../../../company/services/companyAuthService";

// sessionStorage key used to remember which influencer Instagram profile the
// brand wanted to open, so it can be opened automatically once they finish
// connecting their own Instagram account (survives the OAuth redirect).
const PENDING_INSTAGRAM_OPEN_KEY = "infuber:pendingInfluencerInstagramUrl";
const SOCIAL_ACCOUNTS_PATH = "/company/dashboard/social-accounts";

const ProfileHeader = ({ influencer }) => {
  const navigate = useNavigate();
  const [showContact, setShowContact] = useState(false);
  const [contactInfo, setContactInfo] = useState(null);
  const [contactLoading, setContactLoading] = useState(false);
  const [contactError, setContactError] = useState(null);
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);
  const [shareFeedback, setShareFeedback] = useState("");
  const [isSaved, setIsSaved] = useState(false);
  const [savedStatusLoading, setSavedStatusLoading] = useState(false);
  const [saveActionLoading, setSaveActionLoading] = useState(false);
  const [isInviteWizardOpen, setIsInviteWizardOpen] = useState(false);
  const [avatarLoadError, setAvatarLoadError] = useState(false);
  const [instagramCheckLoading, setInstagramCheckLoading] = useState(false);

  const username = influencer?.username;
  const avatarUrl = (influencer?.avatar && String(influencer.avatar).trim()) || "";
  const showAvatarImage = Boolean(avatarUrl) && !avatarLoadError;

  const avatarInitial = (() => {
    const n = influencer?.name;
    if (!n || typeof n !== "string") return "?";
    const t = n.trim();
    if (!t) return "?";
    return t.charAt(0).toUpperCase();
  })();
  const instagramUsername = username?.replace(/^@/, "");
  const influencerInstagramUrl = instagramUsername
    ? `https://www.instagram.com/${encodeURIComponent(instagramUsername)}/`
    : "";
  const ContactMailto = `mailto:sales@infuber.com?subject=${encodeURIComponent(
    `Influencer contact details request${
      influencer?.name
        ? ` for ${influencer.name}`
        : instagramUsername
        ? ` for @${instagramUsername}`
        : ""
    }`
  )}`;
  const displayRating = formatInfluencerRating(
    getEffectiveInfluencerRating({
      rating: influencer?.rating,
      reviewCount: influencer?.reviewCount,
      isVerified: influencer?.isVerified,
    })
  );
  const reviewLabel = Number(influencer?.reviewCount) === 1 ? "Review" : "Reviews";
  const formattedContentType = influencer?.contentType
    ? String(influencer.contentType)
        .split(/[_\s]+/)
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ")
    : "";

  // Reset contact state when influencer changes
  useEffect(() => {
    setContactInfo(null);
    setContactError(null);
  }, [username]);

  useEffect(() => {
    if (!influencer?.id || !username) {
      setIsSaved(false);
      return;
    }

    let cancelled = false;
    const loadSavedState = async () => {
      setSavedStatusLoading(true);
      try {
        const saved = await getSavedInfluencerStatus({
          influencerId: influencer.id,
          username,
        });
        if (!cancelled) {
          setIsSaved(saved);
        }
      } catch (_) {
        if (!cancelled) {
          setIsSaved(false);
        }
      } finally {
        if (!cancelled) {
          setSavedStatusLoading(false);
        }
      }
    };

    loadSavedState();

    return () => {
      cancelled = true;
    };
  }, [influencer?.id, username]);

  useEffect(() => {
    setAvatarLoadError(false);
  }, [avatarUrl]);

  // Fetch contact info when user expands "Show contact"
  useEffect(() => {
    if (!showContact || !username || contactInfo !== null) return;

    let cancelled = false;

    setContactLoading(true);
    setContactError(null);

    getInfluencerContactInfo(username)
      .then((data) => {
        if (!cancelled) setContactInfo(data || {});
      })
      .catch(() => {
        if (!cancelled) setContactError("Failed to load contact info");
      })
      .finally(() => {
        if (!cancelled) setContactLoading(false);
      });

    return () => {
      cancelled = true;
    };

  }, [showContact, username, contactInfo]);

  useEffect(() => {
    if (!avatarModalOpen) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setAvatarModalOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [avatarModalOpen]);

  const handleShare = async () => {
    const shareUrl = window.location.href;
    const shareTitle = influencer?.name ? `${influencer.name} on Infuber` : "Infuber profile";

    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: "Check out this creator profile.",
          url: shareUrl,
        });
        setShareFeedback("Shared");
        setTimeout(() => setShareFeedback(""), 2000);
        return;
      } catch (error) {
        // User cancellation should not show an error message.
        if (error?.name === "AbortError") return;
      }
    }

    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(shareUrl);
        setShareFeedback("Copied");
        setTimeout(() => setShareFeedback(""), 2000);
        return;
      } catch (_) {
        setShareFeedback("Unable to share");
        setTimeout(() => setShareFeedback(""), 2000);
      }
    }
  };

  // Opens the influencer's Instagram for a brand. If the brand hasn't connected
  // their own Instagram yet, they're sent to the social-accounts page to connect
  // first; the influencer profile then opens automatically after connecting.
  const handleOpenInfluencerInstagram = async () => {
    if (!influencerInstagramUrl || instagramCheckLoading) return;

    setInstagramCheckLoading(true);

    // Open the tab synchronously inside the click gesture so it isn't blocked by
    // the browser's popup blocker once the async status check resolves.
    const igTab = window.open("", "_blank");

    try {
      const status = await socialAccountsService.getInstagramStatus();

      if (status?.connected) {
        if (igTab) {
          igTab.opener = null;
          igTab.location.href = influencerInstagramUrl;
        } else {
          window.open(influencerInstagramUrl, "_blank", "noopener,noreferrer");
        }
        return;
      }

      // Not connected yet: discard the placeholder tab, remember where the brand
      // wanted to go, and route them to connect their Instagram first.
      if (igTab) igTab.close();
      try {
        sessionStorage.setItem(PENDING_INSTAGRAM_OPEN_KEY, influencerInstagramUrl);
      } catch (_) {
        // Ignore storage errors (e.g. private mode); the query param is the fallback.
      }
      navigate(
        `${SOCIAL_ACCOUNTS_PATH}?openInstagram=${encodeURIComponent(
          influencerInstagramUrl
        )}`
      );
    } catch (error) {
      if (igTab) igTab.close();
      console.error("Failed to check Instagram connection status:", error);
    } finally {
      setInstagramCheckLoading(false);
    }
  };

  const handleSaveToggle = async () => {
    if (!influencer?.id || !username || saveActionLoading || savedStatusLoading) {
      return;
    }

    setSaveActionLoading(true);
    try {
      if (isSaved) {
        await unsaveInfluencer({ influencerId: influencer.id, username });
        setIsSaved(false);
      } else {
        await saveInfluencer({ influencerId: influencer.id, username });
        setIsSaved(true);
      }
    } catch (error) {
      console.error("Failed to toggle saved influencer state:", error);
    } finally {
      setSaveActionLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      {/* TOP SECTION */}
      <div className="flex flex-col sm:flex-row lg:gap-6 gap-2 items-start sm:items-center">
        {/* Avatar — tap opens full-size circular preview (same image URL, no extra compression) */}
        <button
          type="button"
          onClick={() => setAvatarModalOpen(true)}
          className="rounded-full shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2"
          aria-label={
            showAvatarImage
              ? `View ${influencer.name} profile photo larger`
              : `${influencer.name} profile — no photo, showing initial`
          }
        >
          {showAvatarImage ? (
            <img
              src={avatarUrl}
              srcSet={`
                 ${avatarUrl}?w=150 150w,
                 ${avatarUrl}?w=300 300w,
                 ${avatarUrl}?w=600 600w
                 `}
              sizes="(max-width: 768px) 96px, 112px"
              alt=""
              className="w-24 h-24 rounded-full object-cover pointer-events-none block"
              draggable={false}
              onError={() => setAvatarLoadError(true)}
            />
          ) : (
            <span
              className="w-24 h-24 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 text-white text-3xl font-semibold flex items-center justify-center pointer-events-none select-none"
              aria-hidden
            >
              {avatarInitial}
            </span>
          )}
        </button>

        {avatarModalOpen && (
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Profile photo"
            className="fixed inset-0 z-[100] flex items-center justify-center p-6 sm:p-10 bg-black/80 backdrop-blur-sm"
            onClick={() => setAvatarModalOpen(false)}
          >
            <button
              type="button"
              onClick={() => setAvatarModalOpen(false)}
              className="absolute top-4 right-4 z-[101] rounded-full p-2 text-white/90 hover:bg-white/10 hover:text-white transition"
              aria-label="Close profile photo"
            >
              <X size={28} strokeWidth={1.75} />
            </button>
            <div
              className="relative rounded-full overflow-hidden shadow-2xl ring-2 ring-white/20"
              style={{
                width: "min(85vw, 22rem)",
                height: "min(85vw, 22rem)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {showAvatarImage ? (
                <img
                  src={avatarUrl}
                  alt=""
                  className="h-full w-full object-cover select-none"
                  draggable={false}
                  decoding="async"
                />
              ) : (
                <div className="h-full w-full flex items-center justify-center bg-gradient-to-br from-orange-400 to-orange-600 text-white text-6xl sm:text-8xl font-semibold select-none">
                  {avatarInitial}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Main Info */}
        <div className="flex-1">
          <h1 className="text-xl lg:text-2xl font-semibold flex flex-wrap items-center gap-2">
            {influencer.name}
            {influencer.isVerified && <ClaimStatusBadge isVerified />}
            <span className="text-yellow-400">★ {displayRating}</span>
            <span className="underline text-sm text-gray-500">
              {influencer.reviewCount ?? 0} {reviewLabel}
            </span>
          </h1>

          {/* Influencer username (link to Instagram profile) */}
          {instagramUsername && (
            <a
              href={`https://www.instagram.com/${encodeURIComponent(instagramUsername)}/`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-0 inline-flex items-center gap-1 text-sm font-medium text-orange-500 hover:underline"
            >
              {/* <FaInstagram size={16} /> */}
              @{instagramUsername} <ExternalLink size={16} />
            </a>
          )}

          <p className="text-gray-500 mt-1 text-xs md:text-sm">{influencer.location}</p>
          {formattedContentType && (
            <p className="text-xs md:text-sm text-gray-500 mt-1 font-medium">
              Content Type: <span className="text-orange-500">{formattedContentType}</span>
            </p>
          )}

          {/* Followers */}
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <span className="inline-flex items-center gap-2 border px-2 py-1 rounded-full text-xs md:text-sm text-orange-500">
              <FaInstagram size={16} />
              {formatCompactNumber(influencer.followers)} Followers
            </span>
            <span className="inline-flex items-center gap-2 border px-2 py-1 rounded-full text-xs md:text-sm text-orange-500">
              <FaInstagram size={16} />
              {formatCompactNumber(influencer.following)} Following
            </span>

            {/* <span className="inline-flex items-center gap-2 border px-3 py-1 rounded-full text-xs md:text-sm text-orange-500">
              <Youtube size={16} />
              {influencer.analytics?.YouTube?.followers} Followers
            </span> */}
          </div>

          {/* Contact Toggle */}
          <button
            type="button"
            onClick={() => setShowContact(prev => !prev)}
            className="mt-2 inline-flex items-center gap-1 text-xs md:text-sm font-medium text-gray-700 hover:underline"
          >
            {showContact ? "Hide contact" : "Show contact"}
            {showContact ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>

          {/* Contact Info (fetched on "Show contact") */}
          {showContact && (
            <div className="mt-2 space-y-1">
              {contactLoading && (
                <p className="text-xs md:text-sm text-gray-500">Loading contact info…</p>
              )}
              {contactError && (
                <p className="text-xs md:text-sm text-red-600">{contactError}</p>
              )}
              {!contactLoading && !contactError && contactInfo && (
                <>
                  {contactInfo.email && (
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <Mail size={16} />
                      <a href={`mailto:${contactInfo.email}`} className="hover:underline">
                        {contactInfo.email}
                      </a>
                    </div>
                  )}
                  {contactInfo.mobile && (
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <Phone size={16} />
                      <a href={`tel:${contactInfo.mobile}`} className="hover:underline">
                        {contactInfo.mobile}
                      </a>
                    </div>
                  )}
                  {!contactInfo.email && !contactInfo.mobile && (
                    <>
                      <div className="flex flex-wrap items-center gap-2 text-sm text-gray-500">
                        <span>Best way to reach me: DM</span>
                        {influencerInstagramUrl && (
                          <button
                            type="button"
                            onClick={handleOpenInfluencerInstagram}
                            disabled={instagramCheckLoading}
                            className="inline-flex items-center gap-1 text-sm font-medium text-orange-500 leading-none hover:underline disabled:opacity-60 disabled:cursor-not-allowed"
                            aria-label="View influencer Instagram profile"
                          >
                            {instagramCheckLoading ? (
                              <Loader2 size={16} className="animate-spin" />
                            ) : (
                              <FaInstagram size={16} />
                            )}
                            View Instagram profile
                            <ExternalLink size={16} />
                          </button>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">
                        We can help to get the influencer contact details for a small fee.{" "}
                        <a
                          href={ContactMailto}
                          className="font-medium text-orange-500 hover:underline"
                        >
                          Contact us
                        </a>
                      </p>
                    </>
                  )}
                </>
              )}

              
            </div>
          )}
        </div>

        {/* ACTIONS (RIGHT SIDE) */}
        <div className="flex gap-4 sm:ml-auto">
          <button
            type="button"
            onClick={handleShare}
            className="flex items-center gap-1 text-sm text-gray-700 hover:underline"
            aria-label="Share profile"
          >
            <Share2 size={18} />
            {shareFeedback || "Share"}
          </button>

          <button
            type="button"
            onClick={handleSaveToggle}
            disabled={saveActionLoading || savedStatusLoading}
            className="flex items-center gap-1 text-sm text-gray-700 hover:underline disabled:opacity-70 disabled:cursor-not-allowed"
            aria-label={isSaved ? "Unsave influencer" : "Save influencer"}
          >
            {saveActionLoading || savedStatusLoading ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Heart
                size={18}
                className={isSaved ? "text-red-500 fill-red-500" : ""}
              />
            )}
            {isSaved ? "Saved" : "Save"}
          </button>

          <button
            type="button"
            onClick={() => setIsInviteWizardOpen(true)}
            className="px-2 md:px-5 py-2 rounded-xl text-white text-xs md:text-sm font-medium bg-gradient-to-r from-pink-500 to-purple-600 hover:opacity-90 transition"
          >
            + Invite to Campaign
          </button>
        </div>
      </div>

      {/* BIO */}
      <p className="text-gray-700 max-w-3xl text-xs md:text-sm">
        {influencer.bio}
      </p>

      <InviteInfluencerWizard
        isOpen={isInviteWizardOpen}
        onClose={() => setIsInviteWizardOpen(false)}
        influencer={influencer}
      />
    </div>
  );
};

export default ProfileHeader;
